import type { Page } from '@hello-aria/api-client'
import { toListQuery } from '@hello-aria/makalu-api-client'
import { QueryStringInput } from '@hello-aria/query-string'
import { isEmpty, isNil } from 'lodash'
import { Company } from '../models/companyModel'
import { CreateSubscription, DeleteSubscription, EditSubscription, Subscription } from '../models/subscriptionModels'
import { trackEvent } from '../utils/analyticsUtils'
import { makaluApi } from '../utils/makaluApiClient'

export const listSubscriptions = (companyId: Company['id'], query: QueryStringInput): Promise<Page<Subscription>> =>
    makaluApi.listPage('/companies/{companyId}/subscriptions', {
        params: { path: { companyId }, query: toListQuery(query) }
    })

// A subscription opens from the URL, so an id can be missing. The query then resolves nothing, as before.
export const getSubscription = async (
    companyId: string | undefined,
    subscriptionId: string | undefined
): Promise<Subscription | undefined> => {
    if (isNil(companyId) || isNil(subscriptionId)) return undefined

    return makaluApi.GET('/companies/{companyId}/subscriptions/{subscriptionId}', {
        params: { path: { companyId, subscriptionId } }
    })
}

// A webhook without a secret is sent without the secret field.
export const createSubscription = async (
    companyId: Company['id'],
    payload: CreateSubscription
): Promise<Subscription> => {
    const body: CreateSubscription = isEmpty(payload.options.secret)
        ? { ...payload, options: { endpoint: payload.options.endpoint } }
        : payload
    const subscription = await makaluApi.POST('/companies/{companyId}/subscriptions/webhook', {
        params: { path: { companyId } },
        body
    })

    trackEvent('Create_Subscription')

    return subscription
}

export const updateSubscription = async (
    companyId: Company['id'],
    payload: EditSubscription
): Promise<Subscription> => {
    const { subscriptionId, options } = payload
    const secret = isEmpty(options.secret) ? undefined : options.secret
    const subscription = await makaluApi.PATCH('/companies/{companyId}/subscriptions/webhook/{subscriptionId}', {
        params: { path: { companyId, subscriptionId } },
        body: { options: { ...options, secret } }
    })

    trackEvent('Update_Subscription')

    return subscription
}

export const deleteSubscription = async (
    companyId: Company['id'],
    { subscriptionId }: DeleteSubscription
): Promise<void> => {
    await makaluApi.DELETE('/companies/{companyId}/subscriptions/{subscriptionId}', {
        params: { path: { companyId, subscriptionId } }
    })

    trackEvent('Delete_Subscription')
}
